import React, {
  createContext,
  useContext,
  ChangeEvent,
  useState,
  KeyboardEvent
} from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Avatar from 'components/base/Avatar';
import RevealDropdown from 'components/base/RevealDropdown';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPen, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { UilOutgoingCall, UilIncomingCall } from '@iconscout/react-unicons';
import SearchBox from 'components/common/SearchBox';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import {
  HVACampaignWithBXP,
  createHVABXPCampaign,
  updateHVABXPCampaign,
  deleteHVABXPCampaign
} from 'services/hvaservicecampaign.service';
// eslint-disable-next-line
import CampaignWizardForm, { WizardFormData } from 'components/forms/CampaignWizardForm';

interface HVABXPCampaignsTableProps {
  campaigns: HVACampaignWithBXP[];
  onCampaignsChange: () => Promise<void>;
}

const CampaignsChangeContext = createContext<(() => Promise<void>) | undefined>(
  undefined
);

const useCampaignsChange = () => {
  const context = useContext(CampaignsChangeContext);
  if (context === undefined) {
    throw new Error(
      'useCampaignsChange must be used within a CampaignsChangeProvider'
    );
  }
  return context;
};

type CampaignActionCellProps = {
  campaign: HVACampaignWithBXP;
};

const transformCampaignToWizardData = (
  campaign: HVACampaignWithBXP
): Partial<WizardFormData> => {
  return {
    ...campaign,
    timeschedule: {
      id: Number(campaign.timeschedule),
      shortname: campaign.timeschedulename
    },
    mediagroup: {
      id: Number(campaign.mediagroup),
      shortname: campaign.mediagroupname
    },
    greeting: {
      id: Number(campaign.greeting),
      shortname: campaign.greetingname
    },
    active: Boolean(campaign.active),
    callrecording: Boolean(campaign.callrecording),
    amd: Boolean(campaign.amd),
    serviceid: Number(campaign.serviceid),
    campaigntype: Number(campaign.campaigntype),
    inbound_lookup_method: campaign.inbound_lookup_method || 'DATABASE'
  };
};

const CampaignActionCell: React.FC<CampaignActionCellProps> = ({
  campaign
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // eslint-disable-next-line
  const [initialFormData, setInitialFormData] = useState<Partial<WizardFormData> | undefined>(undefined);
  const onCampaignsChange = useCampaignsChange();

  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleShowDeleteModal = () => setShowDeleteModal(true);

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setInitialFormData(undefined);
  };
  const handleShowEditModal = () => {
    const initialData = transformCampaignToWizardData(campaign);
    setInitialFormData(initialData);
    setShowEditModal(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteHVABXPCampaign(campaign.bxpid);
      await onCampaignsChange();
    } catch (error) {
      alert(`Error deleting BXP Campaign: ${campaign.bxpid} ` + error);
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleEditSubmit = async (updatedCampaign: WizardFormData) => {
    try {
      await updateHVABXPCampaign(campaign.bxpid, updatedCampaign);
      // console.log(`Campaign with ID: ${campaign.id} updated successfully`);
      await onCampaignsChange();
    } catch (error) {
      console.error(`Error updating Campaign with ID: ${campaign.id}`, error);
    } finally {
      handleCloseEditModal();
    }
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={handleShowEditModal}>
          <FontAwesomeIcon icon={faUserPen} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        <Dropdown.Item onClick={handleShowDeleteModal} className="text-danger">
          <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
          Remove
        </Dropdown.Item>
      </RevealDropdown>

      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this Campaign?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Campaign</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-6">
          <CampaignWizardForm
            initialData={initialFormData}
            renderFooter={(isLastStep, formData) => (
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseEditModal}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (isLastStep) {
                      handleEditSubmit(formData);
                    }
                  }}
                  disabled={!isLastStep}
                >
                  {isLastStep ? 'Update Campaign' : '...'}
                </Button>
              </Modal.Footer>
            )}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export const hvacampaignsTableColumns: ColumnDef<HVACampaignWithBXP>[] = [
  {
    accessorKey: 'name',
    header: 'Campaign',
    cell: ({ row: { original } }) => {
      const { name, brand } = original;
      const [imageSrc, setImageSrc] = useState(`/brands/${brand}.png`);

      const handleImageError = () => {
        setImageSrc('/brands/default.png');
      };

      return (
        <div className="d-flex align-items-center text-body-emphasis">
          {imageSrc && (
            <Avatar
              src={imageSrc}
              size="s"
              alt={name}
              onError={handleImageError}
            />
          )}
          &nbsp;{name}
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '5%' }, className: 'pe-2 ps-0 fs-10' },
      cellProps: { className: 'p-0 fw-bold white-space-nowrap' }
    }
  },
  {
    accessorKey: 'action',
    enableSorting: false,
    header: '',
    cell: ({ row: { original } }) => {
      return <CampaignActionCell campaign={original} />;
    },
    meta: {
      cellProps: { className: 'text-body-tertiary' }
    }
  },
  {
    accessorKey: 'id',
    header: 'ID',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'bxpid',
    header: 'BXP ID',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'campaigntypeoutbound',
    header: 'Type',
    cell: ({ row: { original } }) => {
      const { campaigntypeoutbound } = original;
      return (
        <div className="d-flex mx-2 align-items-center text-body-quaternary">
          {campaigntypeoutbound ? (
            <UilOutgoingCall size="18" className="text-primary" />
          ) : (
            <UilIncomingCall size="18" className="text-success" />
          )}
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'text-body-tertiary px-1' }
    }
  },
  {
    accessorKey: 'description',
    header: 'Description',
    meta: {
      headerProps: { style: { width: '80%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'text-body-tertiary px-1' }
    }
  },
  {
    accessorKey: 'bxpname',
    header: 'BXP Name',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'inbound_lookup_method',
    header: 'Inbound Lookup',
    cell: ({ row: { original } }) => {
      return (
        <div className="d-flex justify-content-center align-items-center">
          <span
            className={`badge text-secondary ${
              original.inbound_lookup_method === 'API'
                ? 'bg-info-subtle'
                : 'bg-secondary-subtle'
            }`}
          >
            {original.inbound_lookup_method}
          </span>
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'callernumber',
    header: 'Call Number',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'timeschedule',
    header: 'Schedule',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'callrecording',
    header: 'Recording',
    cell: ({ row: { original } }) => (original.callrecording ? 'Yes' : 'No'),
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'mediagroup',
    header: 'Media',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'routingdn',
    header: 'Routing',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'amd',
    header: 'AMD',
    cell: ({ row: { original } }) => (original.amd ? 'Yes' : 'No'),
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'greeting',
    header: 'Greeting',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'active',
    header: 'Active',
    cell: ({ row: { original } }) => (original.active ? 'Yes' : 'No'),
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  }
];

export const HVABXPCampaignsTable: React.FC<HVABXPCampaignsTableProps> = ({
  campaigns,
  onCampaignsChange
}) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const table = useAdvanceTable({
    data: campaigns,
    columns: hvacampaignsTableColumns,
    pageSize: 5,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleShowAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => setShowAddModal(false);

  const handleAddCampaign = async (formData: WizardFormData) => {
    try {
      await createHVABXPCampaign(formData);
      await onCampaignsChange();
      handleCloseAddModal();
    } catch (error) {
      console.error('Error creating new campaign:', error);
    }
  };

  return (
    <CampaignsChangeContext.Provider value={onCampaignsChange}>
      <AdvanceTableProvider {...table}>
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3">
            <SearchBox
              placeholder="Search Campaigns"
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyPress}
            />
            <div className="ms-auto">
              <Button
                variant="primary"
                className="p-2"
                onClick={handleShowAddModal}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2 fs-8" />
                Create New Campaign
              </Button>
            </div>
          </div>
        </div>
        <div>
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
          <AdvanceTableFooter pagination />
        </div>

        <Modal
          show={showAddModal}
          onHide={handleCloseAddModal}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Create New Campaign</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <CampaignWizardForm
              renderFooter={(isLastStep, formData) => (
                <Modal.Footer>
                  <Button variant="secondary" onClick={handleCloseAddModal}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      if (isLastStep) {
                        handleAddCampaign(formData);
                      }
                    }}
                    disabled={!isLastStep}
                  >
                    {isLastStep ? 'Create Campaign' : '...'}
                  </Button>
                </Modal.Footer>
              )}
            />
          </Modal.Body>
        </Modal>
      </AdvanceTableProvider>
    </CampaignsChangeContext.Provider>
  );
};
